import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DesktopLayout } from '@/components/desktop/DesktopLayout';
import { useAuth } from '@/contexts/AuthContext';
import { Toast } from '@/components/common/Toast';
import { supabase } from '@/lib/supabase';

// Desktop components following SRP
import { SignupHeader } from '@/components/desktop/auth/signup/SignupHeader';
import { PhoneVerification } from '@/components/desktop/auth/signup/buyer/PhoneVerification';
import { AccountForm } from '@/components/desktop/auth/signup/buyer/AccountForm';
import { AgreementSection } from '@/components/desktop/auth/signup/buyer/AgreementSection';

interface FormData {
  phone: string;
  email: string;
  password: string;
  name: string;
  referralCode?: string;
}

interface Agreement {
  allAgree: boolean;
  terms: boolean;
  privacy: boolean;
  age: boolean;
  privacyDelegate: boolean;
  location?: boolean;
  marketing?: boolean;
}

export function DesktopBuyerSignupPage() {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  
  const [formData, setFormData] = useState<FormData>({
    phone: '',
    email: '',
    password: '',
    name: '',
    referralCode: ''
  });
  
  const [agreement, setAgreement] = useState<Agreement>({
    allAgree: false,
    terms: false,
    privacy: false,
    age: false,
    privacyDelegate: false,
    location: false,
    marketing: false
  });

  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState<{ show: boolean; message: string }>({ show: false, message: '' });
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [_verificationToken, setVerificationToken] = useState('');

  const handleTermsView = (type: 'terms' | 'privacy' | 'delegate' | 'location') => {
    // 각 약관 페이지로 새 탭에서 리디렉트
    const titleMap = {
      terms: '서비스이용약관(일반 이용자용)',
      privacy: '개인정보수집이용동의서',
      delegate: '개인정보처리방침',
      location: '위치기반서비스이용약관'
    };
    
    const encodedTitle = encodeURIComponent(titleMap[type]);
    window.open(`/mypage/terms-policies/title/${encodedTitle}`, '_blank');
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAllAgree = () => {
    const newValue = !agreement.allAgree;
    setAgreement({
      allAgree: newValue,
      terms: newValue,
      privacy: newValue,
      age: newValue,
      privacyDelegate: newValue,
      location: newValue,
      marketing: newValue
    });
  };

  const handleAgreementChange = (field: keyof Agreement) => {
    setAgreement(prev => {
      const newAgreement = { ...prev, [field]: !prev[field] };
      
      const requiredChecked = 
        newAgreement.terms && 
        newAgreement.privacy && 
        newAgreement.age && 
        newAgreement.privacyDelegate;
      
      const optionalChecked = 
        newAgreement.location && 
        newAgreement.marketing;
      
      newAgreement.allAgree = requiredChecked && (optionalChecked || false);
      
      return newAgreement;
    });
  };

  const handleVerificationComplete = (verified: boolean, token?: string) => {
    setIsEmailVerified(verified);
    if (token) {
      setVerificationToken(token);
    }
  };

  const handleError = (message: string) => {
    setToast({ show: true, message });
  };

  const handleSuccess = (message: string) => {
    setToast({ show: true, message });
  };

  const isFormValid = () => {
    const requiredFieldsFilled = formData.phone && formData.email && formData.password && formData.name;
    const passwordValid = formData.password && formData.password.length >= 8;
    
    const requiredAgreed = 
      agreement.terms && 
      agreement.privacy && 
      agreement.age && 
      agreement.privacyDelegate;
    
    return requiredFieldsFilled && passwordValid && requiredAgreed && isEmailVerified;
  };

  const handleSignup = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    if (!isFormValid()) {
      setToast({ show: true, message: '필수 항목을 모두 입력하고 약관에 동의해주세요' });
      return;
    }

    setIsLoading(true);
    try {
      const { success, error, user } = await signUp(formData.email, formData.password, formData.name, 'buyer', formData.phone);

      if (success) {
        const referralCode = formData.referralCode?.trim();
        if (user && referralCode) {
          await supabase.rpc('link_referral_code', {
            p_user_id: user.id,
            p_referral_code: referralCode
          });
        }

        setToast({ show: true, message: '회원가입이 완료되었습니다! 에코딜을 이용해 보세요.' });
        setTimeout(() => {
          navigate('/signup/success');
        }, 2000);
      } else {
        setToast({ show: true, message: error || '회원가입에 실패했습니다.' });
      }
    } catch (error) {
      setToast({ show: true, message: '회원가입 중 오류가 발생했습니다' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DesktopLayout>
      <div className="min-h-[80vh] py-12">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
          {/* Header */}
          <div className="px-12 py-8 border-b border-gray-200">
            <SignupHeader 
              title="구매자 회원가입"
              subtitle="에코딜 구매자로 가입하고 할인 혜택을 누리세요!"
            />
          </div>

          {/* Form Content - 2 Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-12">
            {/* Left Column - Form */}
            <div className="space-y-8">
              {/* Account Information */}
              <AccountForm 
                formData={formData}
                onFormChange={handleInputChange}
              />

              {/* Email Verification */}
              <PhoneVerification 
                email={formData.email}
                name={formData.name}
                onVerificationComplete={handleVerificationComplete}
                onError={handleError}
                onSuccess={handleSuccess}
              />
            </div>

            {/* Right Column - Agreement */}
            <div>
              <AgreementSection 
                agreement={agreement}
                onAllAgree={handleAllAgree}
                onAgreementChange={handleAgreementChange}
                onTermsView={handleTermsView}
              />
            </div>
          </div>

          {/* Footer - Submit Button */}
          <div className="px-12 py-8 bg-gray-50 border-t border-gray-200">
            <button
              onClick={handleSignup}
              disabled={!isFormValid() || isLoading}
              className="w-full h-[60px] bg-[#51C854] text-white font-semibold text-[20px] rounded-xl hover:bg-[#46B649] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? '회원가입 중...' : '회원가입 완료'}
            </button>
          </div>
        </div>
      </div>

      {/* Toast Messages */}
      {toast.show && (
        <Toast
          message={toast.message}
          isVisible={toast.show}
          onClose={() => setToast({ show: false, message: '' })}
        />
      )}
    </DesktopLayout>
  );
}
